import type { CallIce, ServerMessage } from '@elena/shared';
import { api } from './api';
import { engineLabels, serverRecognizer } from './listen';
import { tapStream } from './mic';
import { session } from './session';
import { openSession, SttFailure, type CloudEngine, type SttSession } from './sttEngines';
import { FRAME, SAMPLE_RATE, SpeechTracker } from './vad';

/**
 * Elena's phone (#/caller): the voice of the ADMIN LINE calls Elena makes for you. It runs in headless
 * Chrome on the VPS (deploy/docker-compose.yml, service "caller") or in any Chrome tab you leave open.
 *
 *   call_start  the player picked up: answer their WebRTC offer (TURN from the admin panel); what goes out
 *               is Elena's voice, mixed in an AudioContext, never a microphone
 *   player      their audio → the server's recognizer, sentence by sentence → call_heard
 *   call_say    Elena's next sentence: /api/tts (ElevenLabs or Azure), played into the call in order
 *   barge-in    the player talks over Elena: she stops and listens
 */

export type PhoneState = 'offline' | 'ready' | 'ringing' | 'talking';

export interface PhoneStatus {
  state: PhoneState;
  player: string | null;
  lastHeard: string | null;
  lastSaid: string | null;
  error: string | null;
}

/** A pause this long after the player's words ends their sentence. */
const END_OF_SENTENCE_MS = 900;
/** One sentence is cut here at the latest. */
const MAX_SENTENCE_MS = 20_000;
/** Player speech this long while Elena talks = they interrupt her. */
const BARGE_IN_MS = 450;
/** WebRTC must connect within this after the answer. */
const CONNECT_TIMEOUT_MS = 25_000;
/** How often a call reports how it sounds (server log "elena phone"). */
const REPORT_MS = 5000;

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const round = (v: number): number => Math.round(v * 1000) / 1000;

/** The player's voice is raised so its peaks reach about this (0..1)... */
const TARGET_PEAK = 0.5;
/** ...by at most this much (~18 dB), so a silent line's hiss stays below the speech threshold. */
const MAX_GAIN = 8;
/** How fast the remembered peak fades per 20 ms frame (~7 s half-life). */
const LOUDNESS_DECAY = 0.998;
const MIN_LOUDNESS = TARGET_PEAK / MAX_GAIN;
/**
 * Speech threshold (RMS) on the player's unraised line. Game voice peaks 0.1–0.3 when talking
 * (RMS ~0.02+) and its hiss stays under 0.002, so this sits well between them.
 */
const PLAYER_SPEECH_FLOOR = 0.006;

/** Waits until every ICE candidate is in the SDP (non-trickle, like the panel's engine). */
function gathered(pc: RTCPeerConnection, ms: number): Promise<void> {
  return new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') return resolve();
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    };
    const check = () => pc.iceGatheringState === 'complete' && done();
    pc.addEventListener('icegatheringstatechange', check);
    setTimeout(done, ms);
  });
}

/** An AudioContext that runs on its own clock, no speakers needed (Chrome 110+); a normal one elsewhere. */
function silentContext(): AudioContext {
  try {
    return new AudioContext({ sinkId: { type: 'none' } } as AudioContextOptions);
  } catch {
    return new AudioContext();
  }
}

class CallMedia {
  private pc: RTCPeerConnection | null = null;
  private readonly ctx: AudioContext;
  private readonly out: MediaStreamAudioDestinationNode;
  /** Elena's voice also to the speakers of this tab, when someone listens in. */
  private readonly monitor: GainNode;
  private tap: AudioWorkletNode | null = null;
  private queue: string[] = [];
  private playing: AudioBufferSourceNode | null = null;
  private speaking = false;
  private ended = false;
  private stt: SttSession | null = null;
  private tracker = new SpeechTracker(PLAYER_SPEECH_FLOOR);
  private sentenceStart = 0;
  private bargeFrames = 0;
  /** The player's frames of the sentence being recognized, for a second recognizer if the first fails. */
  private sentence: Int16Array[] = [];
  /** Recent peak of the player's line, and the gain it gets (see louder). */
  private loudness = TARGET_PEAK / MAX_GAIN;
  private gain = 1;
  private interrupted = false;
  private recognizer: Awaited<ReturnType<typeof serverRecognizer>> = null;
  private connectTimer: number | undefined;
  private reportTimer: number | undefined;
  /** For call_report: the player's audio that reached the recognizer, and Elena's sentences played. */
  private gotTrack = false;
  private heardFrames = 0;
  private heardPeak = 0;
  private plays = 0;
  /** Sentences the recognizer finished, and how many characters the last one had (0 = empty). */
  private sentences = 0;
  private lastWords = 0;
  /** What Soniox / Scribe answered for the last sentence (see SttSession.debug). */
  private sttDebug: string | null = null;

  constructor(
    readonly callId: string,
    private readonly lang: string,
    private readonly phrases: string[],
    private readonly send: (msg: unknown) => void,
    private readonly update: (patch: Partial<PhoneStatus>) => void,
    listen: boolean,
  ) {
    // Headless Chrome in Docker has no sound card: a context tied to one never runs, so neither Elena's
    // voice nor the player's reaches anyone. With no one listening in, render without an output device.
    this.ctx = listen ? new AudioContext() : silentContext();
    this.out = this.ctx.createMediaStreamDestination();
    this.monitor = this.ctx.createGain();
    this.monitor.gain.value = listen ? 1 : 0;
    this.monitor.connect(this.ctx.destination);
  }

  async answer(sdp: string, ice: CallIce): Promise<void> {
    this.recognizer = await serverRecognizer();
    if (!this.recognizer) {
      this.update({
        error: 'Nema servisa za prepoznavanje govora (Soniox, ElevenLabs ili Azure).',
      });
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume().catch(() => undefined);

    const pc = new RTCPeerConnection({
      iceServers: ice.iceServers,
      iceTransportPolicy: ice.iceTransportPolicy === 'relay' ? 'relay' : 'all',
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
    });
    this.pc = pc;
    void this.report('answer');
    this.reportTimer = window.setInterval(() => void this.report('call'), REPORT_MS);
    for (const track of this.out.stream.getAudioTracks()) pc.addTrack(track, this.out.stream);
    pc.ontrack = (e) => {
      this.gotTrack = true;
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      void this.hear(stream);
      // Chrome only runs a remote WebRTC stream through WebAudio when an element also plays it
      const sink = new Audio();
      sink.muted = true;
      sink.srcObject = stream;
      void sink.play().catch(() => undefined);
      if (this.monitor.gain.value > 0)
        this.ctx.createMediaStreamSource(stream).connect(this.monitor);
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        window.clearTimeout(this.connectTimer);
        this.update({ state: 'talking' });
        this.send({ type: 'call_state', callId: this.callId, state: 'connected' });
      } else if (pc.connectionState === 'failed') {
        this.fail('failed');
      }
    };

    await pc.setRemoteDescription({ type: 'offer', sdp });
    await pc.setLocalDescription(await pc.createAnswer());
    await gathered(pc, 4000);
    const answer = pc.localDescription?.sdp;
    if (!answer) return this.fail('failed');
    this.send({ type: 'call_answer', callId: this.callId, sdp: answer });
    this.connectTimer = window.setTimeout(() => {
      if (pc.connectionState !== 'connected') this.fail('failed');
    }, CONNECT_TIMEOUT_MS);
  }

  // ---------- Elena talks ----------

  say(text: string): void {
    if (this.ended) return;
    this.queue.push(text);
    if (!this.playing) void this.next();
  }

  hush(): void {
    this.queue = [];
    try {
      this.playing?.stop();
    } catch {
      // already stopped
    }
  }

  private async next(): Promise<void> {
    const text = this.queue.shift();
    if (!text || this.ended) {
      if (this.speaking) {
        this.speaking = false;
        this.send({ type: 'call_state', callId: this.callId, state: 'quiet' });
      }
      return;
    }
    let buffer: AudioBuffer;
    try {
      // wav: the Chromium in the caller container has no MP3 decoder
      const blob = await api.tts(text, 'wav');
      buffer = await this.ctx.decodeAudioData(await blob.arrayBuffer());
    } catch (err) {
      this.update({
        error:
          `Glas nije stigao sa servera (/api/tts): ${err instanceof Error ? err.message : String(err)}`.slice(
            0,
            300,
          ),
      });
      return this.next();
    }
    if (this.ended) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.out);
    src.connect(this.monitor);
    this.playing = src;
    if (!this.speaking) {
      this.speaking = true;
      this.send({ type: 'call_state', callId: this.callId, state: 'speaking' });
    }
    this.update({ lastSaid: text });
    src.onended = () => {
      if (this.playing === src) this.playing = null;
      void this.next();
    };
    src.start();
    this.plays++;
  }

  // ---------- the player talks ----------

  private async hear(stream: MediaStream): Promise<void> {
    this.tap = await tapStream(this.ctx, stream, (frame) => this.onFrame(frame));
  }

  private onFrame(raw: Int16Array): void {
    this.heardFrames++;
    let peak = 0;
    for (const s of raw) peak = Math.max(peak, Math.abs(s) / 32768);
    this.heardPeak = Math.max(this.heardPeak, peak);
    if (this.ended || !this.recognizer) return;
    // only the recognizer gets the raised voice: raised hiss would count as talking, and the
    // sentence would never end
    const frame = this.louder(raw, peak);
    const now = performance.now();
    const speech = this.tracker.push(raw, now);

    // talking over Elena: she stops and listens
    if (this.playing && speech) {
      if (++this.bargeFrames * ((FRAME / SAMPLE_RATE) * 1000) >= BARGE_IN_MS) {
        this.bargeFrames = 0;
        this.interrupted = true;
        this.hush();
      }
    } else if (!speech) this.bargeFrames = 0;

    if (!this.stt) {
      this.stt = this.open(this.recognizer.engine);
      this.sentence = [];
      this.sentenceStart = now;
      this.tracker = new SpeechTracker(PLAYER_SPEECH_FLOOR);
    }
    this.stt.push(frame);
    this.sentence.push(frame);

    const heard = this.tracker.heard;
    const long = now - this.sentenceStart > MAX_SENTENCE_MS;
    if ((heard && this.tracker.quietFor(now) > END_OF_SENTENCE_MS) || (long && heard)) {
      this.endSentence();
    } else if (!heard && now - this.sentenceStart > 15_000) {
      // a long silence: start over, so a paid recognizer does not bill a quiet line forever
      const quiet = this.stt;
      this.stt = null;
      quiet.cancel();
      void api.sttUsage({ seconds: quiet.seconds, engine: quiet.engine }).catch(() => undefined);
    }
  }

  private open(engine: CloudEngine): SttSession {
    return openSession(engine, {
      lang: this.lang,
      phrases: this.phrases.length ? this.phrases : (this.recognizer?.phrases ?? []),
      azure: this.recognizer?.azure ?? null,
    });
  }

  /**
   * Game voice chat arrives quiet (peaks around 0.1): below the speech threshold, so Elena never
   * noticed the player talk. Raise it toward TARGET_PEAK by at most MAX_GAIN, following how loud
   * the line has been lately.
   */
  private louder(frame: Int16Array, peak: number): Int16Array {
    this.loudness = Math.max(peak, this.loudness * LOUDNESS_DECAY, MIN_LOUDNESS);
    this.gain = Math.min(MAX_GAIN, Math.max(1, TARGET_PEAK / this.loudness));
    if (this.gain === 1) return frame;
    const out = new Int16Array(frame.length);
    for (let i = 0; i < frame.length; i++) {
      out[i] = Math.max(-32768, Math.min(32767, Math.round((frame[i] ?? 0) * this.gain)));
    }
    return out;
  }

  private endSentence(): void {
    const s = this.stt;
    if (!s) return;
    this.stt = null;
    const recorded = this.sentence;
    this.sentence = [];
    const interrupted = this.interrupted;
    this.interrupted = false;
    /**
     * Soniox / Scribe failed, or gave nothing for a sentence the tracker heard as speech: Azure hears
     * the same recording and keeps listening for the rest of the call. false = no Azure to ask.
     */
    const toAzure = async (why: string): Promise<boolean> => {
      const rec = this.recognizer;
      if (!rec || s.engine === 'azure' || !rec.azure || !recorded.length || this.ended)
        return false;
      this.recognizer = { ...rec, engine: 'azure' };
      this.update({ error: `Prepoznavanje: ${why} Dalje sluša Azure.`.slice(0, 300) });
      const second = this.open('azure');
      for (const pcm of recorded) second.push(pcm);
      try {
        this.heard(second, await second.finish(), interrupted);
      } catch (again) {
        if (again instanceof SttFailure && again.message === 'canceled') return true;
        const whyAgain = again instanceof Error ? again.message : String(again);
        this.update({ error: `Prepoznavanje (Azure): ${whyAgain}`.slice(0, 300) });
      }
      return true;
    };
    s.finish()
      .then(async (guesses) => {
        this.sttDebug = s.debug ?? null;
        if (guesses[0] || !(await toAzure(`${engineLabels[s.engine]} nije vratio tekst.`)))
          this.heard(s, guesses, interrupted);
      })
      .catch(async (err: unknown) => {
        if (err instanceof SttFailure && err.message === 'canceled') return;
        this.sttDebug = s.debug ?? null;
        const why = err instanceof Error ? err.message : String(err);
        if (!(await toAzure(why))) this.update({ error: `Prepoznavanje: ${why}`.slice(0, 300) });
      });
  }

  /** A finished sentence: count the recognizer's seconds and hand the words to the server. */
  private heard(s: SttSession, guesses: string[], interrupted: boolean): void {
    void api.sttUsage({ seconds: s.seconds, engine: s.engine }).catch(() => undefined);
    const [text, ...alternatives] = guesses;
    this.sentences++;
    this.lastWords = text?.length ?? 0;
    void this.report('heard');
    if (!text || this.ended) return;
    this.update({ lastHeard: text });
    this.send({
      type: 'call_heard',
      callId: this.callId,
      text: text.slice(0, 1000),
      ...(alternatives.length ? { alternatives: alternatives.slice(0, 3) } : {}),
      ...(interrupted ? { interrupted: true } : {}),
    });
  }

  // ---------- report ----------

  /**
   * What the server logs as "elena phone": is the audio clock running, do bytes flow both ways, how
   * loud is Elena going out and the player coming in, and did any of the player reach the recognizer.
   */
  private async report(phase: string): Promise<void> {
    const r: Record<string, string | number | boolean | null> = {
      phase,
      chrome: /Chrome\/(\d+)/.exec(navigator.userAgent)?.[1] ?? null,
      audio: this.ctx.state,
      clock: round(this.ctx.currentTime),
      recognizer: this.recognizer?.engine ?? null,
      track: this.gotTrack,
      heardFrames: this.heardFrames,
      heardPeak: round(this.heardPeak),
      gain: round(this.gain),
      talking: this.tracker.heard,
      sentences: this.sentences,
      lastWords: this.lastWords,
      sttDebug: this.sttDebug?.slice(0, 300) ?? null,
      plays: this.plays,
      queued: this.queue.length,
    };
    this.heardPeak = 0;
    const pc = this.pc;
    if (pc) {
      r.conn = pc.connectionState;
      try {
        const stats = await pc.getStats();
        stats.forEach((s: Record<string, unknown>) => {
          if (s.kind !== 'audio' && s.type !== 'candidate-pair') return;
          if (s.type === 'outbound-rtp') {
            r.sentBytes = num(s.bytesSent);
            r.sentPackets = num(s.packetsSent);
          } else if (s.type === 'media-source') {
            r.outLevel = round(num(s.audioLevel));
          } else if (s.type === 'inbound-rtp') {
            r.gotBytes = num(s.bytesReceived);
            r.gotPackets = num(s.packetsReceived);
            r.inLevel = round(num(s.audioLevel));
          } else if (s.type === 'candidate-pair' && s.nominated && s.state === 'succeeded') {
            const local = stats.get(String(s.localCandidateId)) as { candidateType?: string };
            const remote = stats.get(String(s.remoteCandidateId)) as { candidateType?: string };
            r.route = `${local?.candidateType ?? '?'}→${remote?.candidateType ?? '?'}`;
          }
        });
      } catch {
        // closed in the meantime
      }
    }
    if (!this.ended) this.send({ type: 'call_report', callId: this.callId, report: r });
  }

  // ---------- end ----------

  private fail(state: 'failed' | 'ended'): void {
    if (this.ended) return;
    this.send({ type: 'call_state', callId: this.callId, state });
    this.close();
  }

  close(): void {
    if (this.ended) return;
    this.ended = true;
    window.clearTimeout(this.connectTimer);
    window.clearInterval(this.reportTimer);
    this.hush();
    this.stt?.cancel();
    this.stt = null;
    this.tap?.disconnect();
    this.pc?.close();
    void this.ctx.close().catch(() => undefined);
  }
}

/** The phone's own connection to the Elena server, as the device "caller". */
export class CallerPhone {
  private ws: WebSocket | null = null;
  private calls = new Map<string, CallMedia>();
  private retry: number | undefined;
  private ping: number | undefined;
  private stopped = false;
  private attempt = 0;
  status: PhoneStatus = {
    state: 'offline',
    player: null,
    lastHeard: null,
    lastSaid: null,
    error: null,
  };

  constructor(
    private readonly onStatus: (s: PhoneStatus) => void,
    /** Play Elena and the player on this tab's speakers too (a person listening in). */
    private readonly listen = false,
  ) {}

  private update(patch: Partial<PhoneStatus>): void {
    this.status = { ...this.status, ...patch };
    this.onStatus(this.status);
  }

  start(): void {
    this.stopped = false;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(
      `${proto}://${location.host}/ws?token=${encodeURIComponent(session.token() ?? '')}`,
    );
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      ws.send(
        JSON.stringify({
          type: 'hello',
          deviceId: 'elena-phone',
          kind: 'caller',
          name: 'Elenin telefon',
          capabilities: [],
        }),
      );
      this.ping = window.setInterval(() => this.send({ type: 'ping' }), 25_000);
    };
    ws.onmessage = (e) => {
      try {
        this.onMessage(JSON.parse(String(e.data)) as ServerMessage);
      } catch {
        // not for us
      }
    };
    ws.onclose = () => {
      window.clearInterval(this.ping);
      this.update({ state: 'offline' });
      if (this.stopped) return;
      this.retry = window.setTimeout(
        () => this.start(),
        Math.min(15_000, 1000 * 2 ** this.attempt++),
      );
    };
  }

  stop(): void {
    this.stopped = true;
    window.clearTimeout(this.retry);
    this.calls.forEach((c) => c.close());
    this.calls.clear();
    this.ws?.close();
  }

  private send(msg: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private onMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        this.update({ state: this.calls.size ? 'talking' : 'ready', error: null });
        return;
      case 'call_start': {
        this.calls.get(msg.callId)?.close();
        const media = new CallMedia(
          msg.callId,
          msg.lang,
          msg.phrases,
          (m) => this.send(m),
          (p) => {
            this.update(p);
            // nobody sees the headless page: errors go to the server log too
            if (p.error)
              this.send({ type: 'call_report', callId: msg.callId, report: { error: p.error } });
          },
          this.listen,
        );
        this.calls.set(msg.callId, media);
        this.update({
          state: 'ringing',
          player: msg.player,
          lastHeard: null,
          lastSaid: null,
          error: null,
        });
        media.answer(msg.sdp, msg.ice).catch((err: unknown) => {
          this.update({ error: `WebRTC: ${err instanceof Error ? err.message : String(err)}` });
          this.send({ type: 'call_state', callId: msg.callId, state: 'failed' });
          media.close();
        });
        return;
      }
      case 'call_say':
        this.calls.get(msg.callId)?.say(msg.text);
        return;
      case 'call_hush':
        this.calls.get(msg.callId)?.hush();
        return;
      case 'call_end':
        this.calls.get(msg.callId)?.close();
        this.calls.delete(msg.callId);
        this.update({ state: this.calls.size ? 'talking' : 'ready', player: null });
        return;
    }
  }
}
